/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import type { ProcessedData } from "@/lib/package-processing-core";
import { runProcessing } from "@/lib/package-processing-core";

export {
  findMatchesInText,
  runProcessingFromFiles,
  sanitizeForSearch,
} from "@/lib/package-processing-core";

type WorkerRequest = {
  type: "PROCESS";
  payload: {
    messagesFolder: FileSystemDirectoryHandle;
  };
};

type WorkerResponse =
  | {
      type: "SUCCESS";
      payload: ProcessedData;
    }
  | {
      type: "ERROR";
      payload: {
        message: string;
        details?: unknown;
      };
    };

export async function processDataPackage(
  messagesFolder: FileSystemDirectoryHandle,
): Promise<ProcessedData> {
  if (typeof Worker === "undefined") {
    return runProcessing(messagesFolder);
  }

  let worker: Worker | null = null;

  try {
    worker = new Worker(
      new URL("../workers/process-package.worker.ts", import.meta.url),
      { type: "module" },
    );
  } catch (error) {
    console.warn("Failed to spawn package worker:", error);
    return runProcessing(messagesFolder);
  }

  if (!worker) {
    return runProcessing(messagesFolder);
  }

  const workerInstance = worker;

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      workerInstance.terminate();
      worker = null;
    };

    const onMessage = (event: MessageEvent<WorkerResponse>) => {
      const { data } = event;
      if (!data) return;

      if (data.type === "SUCCESS") {
        cleanup();
        resolve(data.payload);
      } else if (data.type === "ERROR") {
        cleanup();
        const err = new Error(data.payload.message);
        (err as Error & { details?: unknown }).details = data.payload.details;
        reject(err);
      }
    };

    const onError = (event: ErrorEvent) => {
      cleanup();
      reject(
        event.error ??
          new Error(event.message || "Package processing worker error"),
      );
    };

    workerInstance.addEventListener("message", onMessage);
    workerInstance.addEventListener("error", onError);

    try {
      workerInstance.postMessage({
        type: "PROCESS",
        payload: { messagesFolder },
      } as WorkerRequest);
    } catch (error) {
      cleanup();
      console.warn(
        "Worker could not receive the directory handle, falling back to main thread",
        error,
      );
      runProcessing(messagesFolder).then(resolve).catch(reject);
    }
  });
}
